import {Tooltip} from 'react-tooltip';
import {AvatarStackProps} from './types';

import {isToday, parseISO} from 'date-fns';

import {FlexCol} from '@quarks';
import {useId, useMemo} from 'react';
import {GroupAvatar} from '../Avatar/GroupAvatar';
import {cn} from '@utils';
import {Avatar} from '../Avatar/Avatar';
import {Group, useAppSelector} from '@lib/store';
import {UserProps} from '../Avatar/types';
import {useTranslation} from 'react-i18next';

const isUser = (
  userOrGroup:
    | UserProps
    | (Group & {
        workdayCount: number;
      }),
): userOrGroup is UserProps => {
  return !('members' in userOrGroup);
};

export const AvatarStack = ({
  className,
  date,
  disabled,
  max = 6,
  overlap = false,
  style,
  users,
  groupsWithCounts,
  ...props
}: AvatarStackProps) => {
  const {t} = useTranslation();
  const tooltip = useId();
  const currentUserId = useAppSelector((state) => state.data.user.user.id);

  const sortedUsers = useMemo(() => {
    return users.slice().sort((a, b) => {
      if (isUser(a) && a.id === currentUserId) return -1;
      if (isUser(b) && b.id === currentUserId) return 1;
      const nameA = a.name || a.displayName || '';
      const nameB = b.name || b.displayName || '';
      return nameA.localeCompare(nameB);
    });
  }, [users, currentUserId]);

  // Calculate how many user avatars can be shown
  const userAvatarsToShow = sortedUsers.slice(0, max);
  const remainingSlots = max - userAvatarsToShow.length;

  // Groups that can be shown in the remaining slots
  const groupsToShow = useMemo(
    () => (groupsWithCounts && remainingSlots > 0 ? groupsWithCounts.slice(0, remainingSlots) : []),
    [groupsWithCounts, remainingSlots],
  );

  // Users that are not shown
  const hiddenUserCount = users.length - userAvatarsToShow.length;

  // Compose the avatars array
  const avatars = useMemo(
    () => [
      ...userAvatarsToShow,
      ...groupsToShow,
      ...(hiddenUserCount > 0
        ? [
            {
              id: 'overflow',
              name: '',
              initials: `+${hiddenUserCount}`,
              email: '',
            },
          ]
        : []),
    ],
    [userAvatarsToShow, groupsToShow, hiddenUserCount],
  );

  const avatarNames = useMemo(() => {
    const result = Array<string>();

    // Add user names
    sortedUsers.forEach((user) => {
      const name = user.name || user.displayName;
      if (name) {
        result.push(name);
      }
    });

    // Add group names
    if (groupsWithCounts) {
      groupsWithCounts.forEach((group) => {
        if (group.name) {
          result.push(group.name);
        }
      });
    }

    return result;
  }, [groupsWithCounts, sortedUsers]);

  return (
    <FlexCol alignItems="flex-start">
      <div
        data-tooltip-id={tooltip}
        className={cn(
          'flex flex-nowrap isolate items-center',
          {
            'gap-x-2': !overlap,
          },
          className,
        )}
        style={style}
        {...props}>
        {avatars.map((userOrGroup, i) =>
          isUser(userOrGroup) ? (
            <Avatar
              key={`${date}-${userOrGroup.email}-${i}`}
              style={{
                zIndex: max - i,
              }}
              className={cn('shadow-mapiq-base', {
                '-ml-2': i > 0 && overlap,
              })}
              user={userOrGroup}
              disabled={disabled}
              shouldShowCheckIn={isToday(parseISO(date))}
            />
          ) : (
            <GroupAvatar
              group={userOrGroup}
              size="medium"
              style={{
                zIndex: max - i,
              }}
              className={cn('shadow-mapiq-base', {
                '-ml-2': i > 0 && overlap,
              })}
              key={`group-${userOrGroup.id}-${i}`}
              strokeWidth={3}
            />
          ),
        )}
      </div>
      <Tooltip
        id={tooltip}
        clickable={true}
        hidden={false}
        delayShow={250}
        style={{
          zIndex: 99,
          padding: 0,
        }}>
        <div className="max-h-[300px] overflow-x-hidden p-4">
          {avatarNames.map((avatarName, index) => (
            <p
              className="truncate"
              key={`${avatarName}${index}`}>
              {avatarName}
            </p>
          ))}
        </div>
      </Tooltip>
    </FlexCol>
  );
};
